import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Users as UsersIcon, GraduationCap, UserPlus, BookOpen,
  Calendar, CheckSquare, BarChart3, Receipt, AlertCircle, CreditCard,
  Wallet, FolderOpen, Shield, ScrollText, KeyRound,
  Home, Settings as SettingsIcon, LogOut,
} from "lucide-react";
import Logo from "../brand/Logo";
import { useAuth } from "../../contexts/AuthContext";
import { FEATURES } from "../../config/features";

const NAV = [
  { to: "/dashboard", label: "nav.dashboard", icon: LayoutDashboard, module: null, phase: 1 },
  { to: "/families", label: "nav.families", icon: UsersIcon, module: "families", phase: 2 },
  { to: "/students", label: "nav.students", icon: GraduationCap, module: "students", phase: 2 },
  { to: "/leads", label: "nav.leads", icon: UserPlus, module: "leads", phase: 2, feature: "leads" },
  { to: "/classes", label: "nav.classes", icon: BookOpen, module: "classes", phase: 3 },
  { to: "/calendar", label: "nav.calendar", icon: Calendar, module: "calendar", phase: 3 },
  { to: "/attendance", label: "nav.attendance", icon: CheckSquare, module: "attendance", phase: 4 },
  { to: "/grades", label: "nav.grades", icon: BarChart3, module: "grades", phase: 4 },
  { to: "/invoices", label: "nav.invoices", icon: Receipt, module: "invoices", phase: 5 },
  { to: "/defaulters", label: "nav.defaulters", icon: AlertCircle, module: "defaulters", phase: 5, feature: "defaulters" },
  { to: "/sepa", label: "nav.sepa", icon: CreditCard, module: "sepa", phase: 5 },
  { to: "/payroll", label: "nav.payroll", icon: Wallet, module: "payroll", phase: 5, feature: "payroll" },
  { to: "/resources", label: "nav.resources", icon: FolderOpen, module: "resources", phase: 6, feature: "resources" },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "nav.users", icon: UsersIcon, module: "users" },
  { to: "/admin/roles", label: "nav.roles", icon: KeyRound, module: "roles" },
  { to: "/admin/holidays", label: "nav.holidays", icon: Calendar, module: "calendar" },
  { to: "/admin/rooms", label: "nav.rooms", icon: Home, module: "rooms" },
  { to: "/admin/settings", label: "nav.settings", icon: SettingsIcon, module: "users" },
  { to: "/admin/gdpr", label: "nav.gdpr", icon: Shield, module: "users" },
  { to: "/admin/audit", label: "nav.auditLog", icon: ScrollText, module: "audit_log" },
];

function canSee(user, perms, module) {
  if (!module) return true;
  if (user?.role === "admin") return true;
  return perms?.[module]?.view === true;
}

function isFeatureOn(featureKey) {
  if (!featureKey) return true;
  return FEATURES[featureKey] === true;
}

export default function Sidebar({ permissions }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const setLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("eata.lang", lng);
  };

  const linkClass = ({ isActive }) =>
    `group flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
      isActive
        ? "bg-white/10 text-white font-semibold shadow-sm ring-1 ring-white/10"
        : "text-white/65 hover:bg-white/[0.07] hover:text-white"
    }`;

  return (
    <aside className="w-64 shrink-0 bg-brand-navy flex flex-col h-screen sticky top-0" data-testid="app-sidebar">
      <div className="px-5 py-6 border-b border-white/10">
        <Logo variant="white" height={30} data-testid="sidebar-logo" />
        <div className="mt-3 text-white/90">
          <div className="font-heading text-base text-white leading-tight">The English Academy</div>
          <div className="label-eyebrow text-white/45 mt-1">Management Suite</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5" data-testid="sidebar-nav">
        {NAV.filter((n) => isFeatureOn(n.feature) && canSee(user, permissions, n.module)).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.to.slice(1)}`}>
              {({ isActive }) => (
                <>
                  <span className={`flex h-5 w-0.5 rounded-full transition-colors ${isActive ? "bg-brand-red" : "bg-transparent group-hover:bg-white/20"}`} />
                  <Icon size={17} strokeWidth={1.75} className="shrink-0" />
                  <span className="truncate">{t(n.label)}</span>
                </>
              )}
            </NavLink>
          );
        })}

        {(user?.role === "admin") && (
          <>
            <div className="px-5 mt-5 mb-2 label-eyebrow text-white/40 flex items-center gap-2">
              <Shield size={12} /> {t("nav.admin")}
            </div>
            {ADMIN_NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.to.replace(/\//g, "-").slice(1)}`}>
                  {({ isActive }) => (
                    <>
                      <span className={`flex h-5 w-0.5 rounded-full transition-colors ${isActive ? "bg-brand-red" : "bg-transparent group-hover:bg-white/20"}`} />
                      <Icon size={17} strokeWidth={1.75} className="shrink-0" />
                      <span className="truncate">{t(n.label)}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      <div className="border-t border-white/10 p-4 space-y-3">
        <div className="inline-flex items-center gap-1 text-xs rounded-lg bg-white/5 p-0.5">
          <button
            data-testid="lang-en"
            onClick={() => setLang("en")}
            className={`px-2.5 py-1 rounded-md transition-colors ${i18n.language === "en" ? "bg-white text-brand-navy font-semibold" : "text-white/60 hover:text-white"}`}
          >EN</button>
          <button
            data-testid="lang-es"
            onClick={() => setLang("es")}
            className={`px-2.5 py-1 rounded-md transition-colors ${i18n.language === "es" ? "bg-white text-brand-navy font-semibold" : "text-white/60 hover:text-white"}`}
          >ES</button>
        </div>

        <div className="flex items-center gap-3 rounded-lg p-1.5 -m-1.5 hover:bg-white/5 transition-colors" data-testid="user-badge">
          <button
            onClick={() => navigate("/profile")}
            className="h-9 w-9 rounded-full bg-brand-red hover:opacity-90 flex items-center justify-center text-white font-semibold text-sm transition-opacity shrink-0"
            title={t("nav.profile")}
            data-testid="topbar-profile-button"
          >
            {(user?.full_name || user?.email || "?").slice(0, 1).toUpperCase()}
          </button>
          <button onClick={() => navigate("/profile")} className="min-w-0 flex-1 text-left">
            <div className="text-sm text-white truncate">{user?.full_name}</div>
            <div className="label-eyebrow text-white/45">{user?.role}</div>
          </button>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-white/55 hover:text-white p-1.5 rounded-md hover:bg-white/10 transition-colors"
            title={t("common.logout")}
            data-testid="logout-button"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
